import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { bulkInsertFromXlsx, bulkInsertFromXml } from "../api";
import { toast } from "react-toastify";

interface ImportUsersModalProps {
  open: boolean;
  onClose: () => void;
  onRefreshUsers: () => void; // Nova prop para acionar a atualização
}

const ImportUsersModal: React.FC<ImportUsersModalProps> = ({
  open,
  onClose,
  onRefreshUsers, // Recebe a função de atualização
}) => {
  const [loading, setLoading] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultData, setResultData] = useState<any>(null);

  const handleImportXml = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeType = "text/xml";
    const validExtension = ".xml";

    if (file.type !== validMimeType && !file.name.endsWith(validExtension)) {
      toast.error("Por favor, selecione um arquivo XML válido.");
      return;
    }

    setLoading(true);
    const username = sessionStorage.getItem("username") || "";

    try {
      const response = await bulkInsertFromXml(username, file);

      if (
        response.details?.successfullyAddedUsers > 0 &&
        (response.details?.failedUsers?.length > 0 ||
          response.details?.failedPermissions?.length > 0)
      ) {
        toast.info(
          "Importação concluída com sucesso, mas com alguns erros. Verifique os detalhes."
        );
      } else if (
        response.details?.successfullyAddedUsers > 0 &&
        response.details?.failedUsers?.length === 0 &&
        response.details?.failedPermissions?.length === 0
      ) {
        toast.success("Todos os usuários foram importados com sucesso!");
      } else {
        toast.error("Erro ao importar usuários. Verifique os detalhes.");
      }

      setResultData(response);
      onRefreshUsers(); // Atualiza a lista após sucesso
    } catch (error: any) {
      setResultData(
        error.response?.data || { message: "Erro inesperado.", details: {} }
      );

      toast.error(
        error.response?.data?.message ||
          "Erro ao processar o arquivo de importação."
      );
    } finally {
      setLoading(false); // <- ENCERRA o loading ANTES
      setResultModalOpen(true); // <- Mostra o modal de resultado
      onClose(); // <- Só FECHA o modal depois de tudo
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/modelos/modelo_importacao.xlsx"; // Caminho do arquivo modelo (coloque na pasta public)
    link.download = "modelo_importacao.xlsx";
    link.click();
  };

  const handleImportXlsx = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const validExtension = ".xlsx";

    if (file.type !== validMimeType && !file.name.endsWith(validExtension)) {
      toast.error("Por favor, selecione um arquivo XLSX válido.");
      return;
    }

    setLoading(true);
    const username = sessionStorage.getItem("username") || "";

    try {
      const response = await bulkInsertFromXlsx(username, file);

      // Verifica o retorno e exibe os toasts apropriados
      if (
        response.details?.successfullyAddedUsers > 0 &&
        (response.details?.failedUsers?.length > 0 ||
          response.details?.failedPermissions?.length > 0)
      ) {
        // Inserção parcial com erros
        toast.info(
          "Importação concluída com sucesso, mas com alguns erros. Verifique os detalhes."
        );
      } else if (
        response.details?.successfullyAddedUsers > 0 &&
        response.details?.failedUsers?.length === 0 &&
        response.details?.failedPermissions?.length === 0
      ) {
        // Sucesso total
        toast.success("Todos os usuários foram importados com sucesso!");
      } else {
        // Nenhum sucesso
        toast.error("Erro ao importar usuários. Verifique os detalhes.");
      }

      setResultData(response);
      onRefreshUsers(); // Atualiza os usuários apenas após a resposta
    } catch (error: any) {
      // Para status de erro (400, 500, etc.)
      setResultData(
        error.response?.data || { message: "Erro inesperado.", details: {} }
      );

      toast.error(
        error.response?.data?.message ||
          "Erro ao processar o arquivo de importação."
      );
    } finally {
      setLoading(false);
      setResultModalOpen(true);
      onClose();
    }
  };

  const handleDownloadLog = () => {
    if (!resultData) return;

    let logContent = "Resultado da Importação\n\n";

    logContent += `Mensagem: ${resultData.message || "Erro ao processar os dados."}\n\n`;
    logContent += "Resumo:\n";
    logContent += `- Usuários adicionados com sucesso: ${resultData.details?.successfullyAddedUsers || 0}\n`;
    logContent += `- Usuários não encontrados no Windows: ${notFoundUsers.length}\n`;
    logContent += `- Pastas não encontradas: ${resultData.details?.failedPermissions?.reduce(
      (acc: number, curr: any) => acc + (curr.paths?.length || 0),
      0
    )}\n\n`;

    logContent += "Erros Detalhados:\n";

    // --- Usuários não encontrados
    if (notFoundUsers.length > 0) {
      logContent += "\nUsuários não encontrados no sistema Windows:\n";
      notFoundUsers.forEach((user: string) => {
        logContent += `- ${user}\n`;
      });
    }

    // --- Erros de Permissão
    const failedPermissions = resultData.details?.failedPermissions || [];
    if (failedPermissions.length > 0) {
      logContent += "\n\nErros de Permissões:\n";
      failedPermissions.forEach((permissionError: any) => {
        logContent += `\nUsuário: ${permissionError.user}\n`;
        logContent += "Pastas não encontradas:\n";
        permissionError.paths.forEach((path: string) => {
          logContent += `- ${path}\n`;
        });
      });
    }

    const blob = new Blob([logContent], { type: "text/plain;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "log_importacao.txt";
    link.click();
  };

  const handleCloseResultModal = () => {
    setResultModalOpen(false);
    setResultData(null);
  };

  // Função para separar usuários por erro
  const categorizeFailedUsers = () => {
    const failedUsers = resultData?.details?.failedUsers || [];
    const notFoundUsers: string[] = [];
    const alreadyExistsUsers: string[] = [];

    failedUsers.forEach((userError: string) => {
      if (userError.includes("Usuário não encontrado no sistema Windows")) {
        notFoundUsers.push(userError.split(":")[0]); // Captura o login antes do ":"
      } else if (userError.includes("Usuário já está cadastrado no sistema")) {
        alreadyExistsUsers.push(userError.split(":")[0]); // Captura o login antes do ":"
      }
    });

    return { notFoundUsers, alreadyExistsUsers };
  };

  const { notFoundUsers, alreadyExistsUsers } = resultData
    ? categorizeFailedUsers()
    : { notFoundUsers: [], alreadyExistsUsers: [] };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
              Importando usuários, por favor aguarde...
            </Typography>
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        </Box>
      )}

      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Importar Usuários em Volume</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 240, // define largura da box interna
              }}
            >
              <Button
                variant="contained"
                component="label"
                color="primary"
                disabled={loading}
                sx={{ width: "100%" }}
              >
                Importar por XML
                <input
                  type="file"
                  accept=".xml"
                  hidden
                  onChange={handleImportXml}
                />
              </Button>

              <Button
                variant="contained"
                component="label"
                color="success"
                disabled={loading}
                sx={{ width: "100%", mt: 2 }}
              >
                Importar por XLSX
                <input
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={handleImportXlsx}
                />
              </Button>

              <Box
                sx={{
                  width: "100%",
                  mt: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                  onClick={handleDownloadTemplate}
                >
                  Baixar modelo XLSX
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={loading}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultModalOpen}
        onClose={handleCloseResultModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <strong>Processamento Concluído</strong>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {resultData ? (
            <Box>
              <Typography variant="h6">Resultado da Importação</Typography>
              <Typography variant="body1" sx={{ marginTop: 2 }}>
                {resultData.message || "Erro ao processar os dados."}
              </Typography>

              <Box sx={{ marginTop: 2 }}>
                <Typography variant="subtitle1">Resumo:</Typography>
                <Typography>
                  - Usuários adicionados com sucesso:{" "}
                  {resultData.details?.successfullyAddedUsers || 0}
                </Typography>
                <Typography>
                  - Usuários não encontrados no Windows: {notFoundUsers.length}
                </Typography>
                <Typography>
                  - Pastas não encontradas:{" "}
                  {resultData.details?.failedPermissions?.reduce(
                    (acc: number, curr: any) => acc + (curr.paths?.length || 0),
                    0
                  )}
                </Typography>
              </Box>

              {(notFoundUsers.length > 0 ||
                alreadyExistsUsers.length > 0 ||
                resultData?.details?.failedPermissions?.length > 0) && (
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="subtitle1">Erros Detalhados:</Typography>

                  {notFoundUsers.length > 0 && (
                    <Box sx={{ marginTop: 1 }}>
                      <Typography variant="body2">
                        <strong>
                          Usuários não encontrados no sistema Windows:
                        </strong>
                      </Typography>
                      <ul>
                        {notFoundUsers.map((user: string, index: number) => (
                          <li key={index}>
                            <Typography>{user}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  {alreadyExistsUsers.length > 0 && (
                    <Box sx={{ marginTop: 1 }}>
                      <Typography variant="body2">
                        <strong>Usuários já cadastrados no sistema:</strong>
                      </Typography>
                      <ul>
                        {alreadyExistsUsers.map(
                          (user: string, index: number) => (
                            <li key={index}>
                              <Typography>{user}</Typography>
                            </li>
                          )
                        )}
                      </ul>
                    </Box>
                  )}

                  {resultData?.details?.failedPermissions?.length > 0 && (
                    <Box sx={{ marginTop: 1 }}>
                      <Typography variant="subtitle1">
                        Erros de Permissões:
                      </Typography>
                      {resultData.details.failedPermissions.map(
                        (permissionError: any, index: number) => (
                          <Box key={index} sx={{ marginTop: 2 }}>
                            <Typography variant="body2">
                              <strong>Usuário:</strong> {permissionError.user}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Pastas não encontradas:</strong>
                            </Typography>
                            <ul>
                              {permissionError.paths.map(
                                (path: string, idx: number) => (
                                  <li key={idx}>
                                    <Typography>{path}</Typography>
                                  </li>
                                )
                              )}
                            </ul>
                          </Box>
                        )
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography>Erro ao processar os dados.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDownloadLog}>BAIXAR LOG</Button>
          <Button onClick={handleCloseResultModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportUsersModal;
